import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTO_SAVING_TIME } from '../../../constants/common';

export const AUTO_SAVE_STATUS = {
  IDLE: 'idle',
  PENDING: 'pending',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
};

// Debounced auto-save. markDirty() (re)arms the timer, saveNow() fires
// immediately and disarms it. Replaces the autoSaving redux slice: the
// pending/saving/saved dance stays inside the component that edits the data.
const useAutoSave = (onSave, delay = AUTO_SAVING_TIME) => {
  const timerRef = useRef(null);
  const onSaveRef = useRef(onSave);
  const [status, setStatus] = useState(AUTO_SAVE_STATUS.IDLE);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const runSave = useCallback(async () => {
    setStatus(AUTO_SAVE_STATUS.SAVING);
    try {
      await onSaveRef.current();
      setStatus(AUTO_SAVE_STATUS.SAVED);
    } catch (e) {
      console.error(e);
      setStatus(AUTO_SAVE_STATUS.ERROR);
    }
  }, []);

  const markDirty = useCallback(() => {
    cancel();
    setStatus(AUTO_SAVE_STATUS.PENDING);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      runSave();
    }, delay);
  }, [cancel, delay, runSave]);

  const saveNow = useCallback(() => {
    cancel();
    return runSave();
  }, [cancel, runSave]);

  // Never fire a save after the editor unmounts.
  useEffect(() => cancel, [cancel]);

  return { markDirty, saveNow, cancel, status };
};

export default useAutoSave;
